import _extends from "@babel/runtime/helpers/extends";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AuthoritiesEnum, IntegrationTypeEnum, StatusStateEnum, ThemeTypeEnum } from "~/types/enums";
import { Alert, Box, Button, Card, Grid, Picture, Text } from "~/components-shared";
import integrationSelectors from "~/store/integrations/selectors";
import SlackIntegrationCard from "~/components/SlackIntegrationCard";
import NotionIntegrationCard from "~/components/NotionIntegrationCard";
import JiraIntegrationCard from "~/components/JiraIntegrationCard";
import AsanaIntegrationCard from "~/components/AsanaIntegrationCard";
import useAbacExt from "~/hooks/abac";
import companySelectors from "~/store/company/selectors";
import { useTranslation } from "react-i18next";
import LinearIntegrationCard from "~/components/LinearIntegrationCard";
import GithubIntegrationCard from "~/components/GithubIntegrationCard";
import SalesforceIntegrationCard from "~/components/SalesforceIntegrationCard";
import styled, { useTheme } from "styled-components";
import soc2Logo from "~/assets/i/logos/soc-2-rectangle-white.png";
import soc2LogoDark from "~/assets/i/logos/soc-2-rectangle-dark.png";
import ProductboardIntegrationCard from "~/components/ProductboardIntegrationCard";
import companyActionCreators from "~/store/company/actions";
import GongIntegrationCard from "~/components/GongIntegrationCard";
import MondayIntegrationCard from "~/components/MondayIntegrationCard";
import ConfluenceIntegrationCard from "~/components/ConfluenceIntegrationCard";
import GoogleIntegrationCard from "~/components/GoogleIntegrationCard";
const StyledAlert = styled(Alert)`
  position: sticky;
  top: 1em;
  z-index: 2;
`;
const StyledBox = styled(Box)`
  position: relative;

  &:after {
    content: "";
    position: absolute;
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
    z-index: 2;
    background-color: ${_ref => {
  let {
    theme
  } = _ref;
  return theme.colors.background.secondary;
}};
    opacity: 0.5;
  }
`;
const authorities = [AuthoritiesEnum.INTEGRATIONS_READ, AuthoritiesEnum.ONBOARDING_COMPANY_INTEGRATIONS_WRITE, AuthoritiesEnum.SIGN_UP_INTEGRATE];
const integrations = [{
  type: IntegrationTypeEnum.SLACK,
  component: /*#__PURE__*/React.createElement(SlackIntegrationCard, {
    color: "background",
    align: "left",
    shadow: true
  }),
  auth: authorities,
  paid: false
}, {
  type: IntegrationTypeEnum.GOOGLE,
  component: /*#__PURE__*/React.createElement(GoogleIntegrationCard, {
    color: "background",
    align: "left",
    shadow: true
  }),
  auth: authorities,
  paid: false
}, {
  type: IntegrationTypeEnum.JIRA,
  component: /*#__PURE__*/React.createElement(JiraIntegrationCard, {
    color: "background",
    align: "left",
    shadow: true
  }),
  auth: authorities,
  paid: false
}, {
  type: IntegrationTypeEnum.CONFLUENCE,
  component: /*#__PURE__*/React.createElement(ConfluenceIntegrationCard, {
    color: "background",
    align: "left",
    shadow: true
  }),
  auth: authorities,
  paid: true
}, {
  type: IntegrationTypeEnum.LINEAR,
  component: /*#__PURE__*/React.createElement(LinearIntegrationCard, {
    color: "background",
    align: "left",
    shadow: true
  }),
  auth: authorities,
  paid: false
}, {
  type: IntegrationTypeEnum.ASANA,
  component: /*#__PURE__*/React.createElement(AsanaIntegrationCard, {
    color: "background",
    align: "left",
    shadow: true
  }),
  auth: authorities,
  paid: false
}, {
  type: IntegrationTypeEnum.PRODUCTBOARD,
  component: /*#__PURE__*/React.createElement(ProductboardIntegrationCard, {
    color: "background",
    align: "left",
    shadow: true
  }),
  auth: authorities,
  paid: true
}, {
  type: IntegrationTypeEnum.MONDAY,
  component: /*#__PURE__*/React.createElement(MondayIntegrationCard, {
    color: "background",
    align: "left",
    shadow: true
  }),
  auth: authorities,
  paid: true
}, {
  type: IntegrationTypeEnum.SALESFORCE,
  component: /*#__PURE__*/React.createElement(SalesforceIntegrationCard, {
    color: "background",
    align: "left",
    shadow: true
  }),
  auth: authorities,
  paid: true
}, {
  type: IntegrationTypeEnum.GITHUB,
  component: /*#__PURE__*/React.createElement(GithubIntegrationCard, {
    color: "background",
    align: "left",
    shadow: true
  }),
  auth: authorities,
  paid: true
}, {
  type: IntegrationTypeEnum.NOTION,
  component: /*#__PURE__*/React.createElement(NotionIntegrationCard, {
    color: "background",
    align: "left",
    shadow: true
  }),
  auth: authorities,
  paid: true
}, {
  type: IntegrationTypeEnum.GONG,
  component: /*#__PURE__*/React.createElement(GongIntegrationCard, {
    color: "background",
    align: "left",
    shadow: true
  }),
  auth: authorities,
  paid: true
}];
const CompanyIntegrations = _ref2 => {
  let {
    showSoc2Logo,
    filter,
    ...rest
  } = _ref2;
  const {
    t
  } = useTranslation();
  const {
    type
  } = useTheme();
  const {
    userHasOneOf
  } = useAbacExt();
  const status = useSelector(integrationSelectors.getStatus);
  const integrationError = useSelector(integrationSelectors.getError);
  const memberError = useSelector(integrationSelectors.getMemberError);
  const memberStatus = useSelector(integrationSelectors.getMemberStatus);
  const company = useSelector(companySelectors.getCompany);
  const companyStatus = useSelector(companySelectors.getStatus);
  const dispatch = useDispatch();
  const recommendedIntegrations = [];
  const optionalIntegrations = [];
  const paidIntegrations = [];
  const error = integrationError || memberError;
  useEffect(() => {
    if (companyStatus === StatusStateEnum.IDLE) {
      dispatch(companyActionCreators.fetchStarted());
    }
  }, [companyStatus, dispatch]);
  integrations.forEach(integration => {
    if (filter && filter.length > 0 && !filter.includes(integration.type)) {
      return;
    }
    if (company?.settings.preferredIntegrations?.length && integration.paid) {
      paidIntegrations.push(integration);
      return;
    }
    if (company?.settings.recommendedIntegrations?.includes(integration.type) && !filter) {
      recommendedIntegrations.push(integration);
    } else {
      optionalIntegrations.push(integration);
    }
  });
  const handleMeetJoyn = () => {
    window.open("https://calendly.com/kendra-joyn/30-mins", "_blank");
  };
  return /*#__PURE__*/React.createElement(Card, _extends({
    s: {
      p: 0
    },
    loading: status === StatusStateEnum.LOADING || memberStatus === StatusStateEnum.LOADING
  }, rest), error && /*#__PURE__*/React.createElement(StyledAlert, {
    severity: "danger",
    s: {
      mb: 3
    },
    fullWidth: true
  }, error), !!recommendedIntegrations.length && /*#__PURE__*/React.createElement(Box, {
    direction: "column",
    fullWidth: true,
    align: "stretch",
    s: {
      mb: 5
    }
  }, /*#__PURE__*/React.createElement(Text, {
    element: "h6",
    s: {
      mb: 3
    },
    fontSize: 4
  }, t("CompanyIntegrations.recommended")), recommendedIntegrations.map(integration => userHasOneOf(integration.auth) && /*#__PURE__*/React.createElement(React.Fragment, {
    key: integration.type
  }, integration.component))), !!optionalIntegrations.length && /*#__PURE__*/React.createElement(Box, {
    direction: "column",
    align: "stretch",
    s: {
      mb: 5
    }
  }, !!recommendedIntegrations.length && /*#__PURE__*/React.createElement(Text, {
    element: "h6",
    s: {
      mb: 3
    },
    fontSize: 4
  }, t("CompanyIntegrations.optional")), optionalIntegrations.map(integration => userHasOneOf(integration.auth) && /*#__PURE__*/React.createElement(React.Fragment, {
    key: integration.type
  }, integration.component))), !!paidIntegrations.length && /*#__PURE__*/React.createElement(Box, {
    direction: "column",
    align: "stretch"
  }, /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    template: "1fr auto",
    fullWidth: true,
    justify: "start",
    align: "end",
    s: {
      mb: 3
    }
  }, /*#__PURE__*/React.createElement(Text, {
    element: "h6",
    fontSize: 4
  }, t("CompanyIntegrations.paidPlan")), /*#__PURE__*/React.createElement(Button, {
    color: "translucentPrimary",
    s: {
      px: 5,
      py: 1.5
    },
    fontSize: 3.5,
    onClick: handleMeetJoyn
  }, t("CompanyIntegrations.chatSales"))), /*#__PURE__*/React.createElement(StyledBox, {
    direction: "column",
    align: "stretch"
  }, paidIntegrations.map(integration => userHasOneOf(integration.auth) && /*#__PURE__*/React.createElement(React.Fragment, {
    key: integration.type
  }, integration.component)))), showSoc2Logo && /*#__PURE__*/React.createElement(Box, {
    justify: "center",
    s: {
      mt: 8
    }
  }, /*#__PURE__*/React.createElement(Picture, {
    src: type === ThemeTypeEnum.LIGHT ? soc2LogoDark : soc2Logo,
    width: "200px",
    height: "72px",
    border: "squared",
    style: {
      opacity: 0.5
    }
  })));
};
export default CompanyIntegrations;